import isNil from 'lodash/isNil';
import { useEffect, useRef, useState, type ComponentProps } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/components/base/tooltip';
import { Typography } from '@ui/components/base/typography';
import { cn } from '@ui/lib/utils';

type TooltipSide = ComponentProps<typeof TooltipContent>['side'];

export interface OverflowTypographyProps extends ComponentProps<typeof Typography> {
  text: string;
  tooltipSide?: TooltipSide;
}

const OverflowTypography = ({ text, tooltipSide, className, ...props }: OverflowTypographyProps) => {
  const typographyRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    const element = typographyRef.current;
    if (isNil(element)) {
      return undefined;
    }
    const checkOverflow = () => {
      setIsOverflowing(element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight);
    };
    checkOverflow();
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [text]);

  return (
    <TooltipProvider>
      <Tooltip open={isOpen && isOverflowing} onOpenChange={setIsOpen}>
        <TooltipTrigger render={<Typography ref={typographyRef} className={cn('truncate', className)} {...props} />}>
          {text}
        </TooltipTrigger>
        <TooltipContent side={tooltipSide} className="wrap-anywhere">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export { OverflowTypography };
